import React from "react";
import { ChevronUp, ChevronDown, CheckCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { PrescriptionApprovalRow } from "./PrescriptionApprovalRow";

interface PrescriptionApprovalTableProps {
  isDarkMode: boolean;
  prescriptions: any[];
  sortField:
    | "patientName"
    | "consultationDate"
    | "prescribedBy"
    | "serviceType";
  sortDirection: "asc" | "desc";
  onSort: (
    field: "patientName" | "consultationDate" | "prescribedBy" | "serviceType"
  ) => void;
  selectAll: boolean;
  onSelectAll: (checked: boolean) => void;
  selectedPrescriptions: Set<string>;
  onSelectPrescription: (prescriptionId: string, checked: boolean) => void;
  onViewPrescription: (prescriptionImageUrl: string) => void;
  onProcessPrescription: (prescription: any) => void;
  onViewConsultationSummary: (prescription: any) => void;
  onGoToConsultation: (prescription: any) => void;
  onDeclinePrescription: (prescription: any) => void;
}

export function PrescriptionApprovalTable({
  isDarkMode,
  prescriptions,
  sortField,
  sortDirection,
  onSort,
  selectAll,
  onSelectAll,
  selectedPrescriptions,
  onSelectPrescription,
  onViewPrescription,
  onProcessPrescription,
  onViewConsultationSummary,
  onGoToConsultation,
  onDeclinePrescription,
}: PrescriptionApprovalTableProps) {
  return (
    <div
      className={`rounded-lg border ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}>
            <tr>
              <th
                className={`px-3 py-3 text-left ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                }`}
              >
                <Checkbox
                  checked={selectAll}
                  onCheckedChange={(checked) => onSelectAll(!!checked)}
                  aria-label="Select all prescriptions"
                />
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                <button
                  onClick={() => onSort("patientName")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Patient
                  {sortField === "patientName" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    ))}
                </button>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                Medication
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                <button
                  onClick={() => onSort("prescribedBy")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Prescribed By
                  {sortField === "prescribedBy" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    ))}
                </button>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                <button
                  onClick={() => onSort("serviceType")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Service
                  {sortField === "serviceType" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    ))}
                </button>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                <button
                  onClick={() => onSort("consultationDate")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Date
                  {sortField === "consultationDate" &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    ))}
                </button>
              </th>
              <th
                className={`px-6 py-3 text-right text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-500"
                } uppercase tracking-wider`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody
            className={`${isDarkMode ? "bg-gray-800" : "bg-white"} divide-y ${
              isDarkMode ? "divide-gray-700" : "divide-gray-200"
            }`}
          >
            {prescriptions.map((prescription) => (
              <PrescriptionApprovalRow
                key={prescription.id}
                prescription={prescription}
                isDarkMode={isDarkMode}
                isSelected={selectedPrescriptions.has(prescription.id)}
                onSelect={(checked) =>
                  onSelectPrescription(prescription.id, checked)
                }
                onView={() => {
                  onViewPrescription(prescription.id);
                }}
                onProcess={() => onProcessPrescription(prescription)}
                onViewConsultationSummary={() =>
                  onViewConsultationSummary(prescription)
                }
                onGoToConsultation={() => onGoToConsultation(prescription)}
                onDecline={() => onDeclinePrescription(prescription)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {prescriptions.length === 0 && (
        <div className="text-center py-12">
          <CheckCircle
            className={`w-12 h-12 mx-auto mb-4 ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          />
          <h3
            className={`text-lg font-medium mb-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            No pending approvals
          </h3>
          <p className={`${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
            All prescriptions have been reviewed and processed.
          </p>
        </div>
      )}
    </div>
  );
}
